'use client';

import React from 'react';
import Link from 'next/link';
import { ApiRoutes } from '@/settings/routes';
import { SiTripadvisor } from 'react-icons/si';
import { FaQuoteLeft, FaCheckCircle } from 'react-icons/fa';
import { PiSealCheckFill } from 'react-icons/pi';
import StarRating from './StarRating';
import { format } from 'date-fns';

export default function CardReviewCompact({ review }) {
    const principalImage = review?.images?.find(img => img.thumbnail === 1) || review?.images?.[0];

    const truncateComment = (text, maxLength = 150) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <div className="flex overflow-hidden relative flex-col h-full bg-gradient-to-br from-white via-white to-green-50 rounded-2xl shadow-lg transition-all duration-500 hover:shadow-xl group">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-green-100 rounded-full opacity-0 transition-opacity duration-500 group-hover:opacity-100"></div>

            <div className="absolute top-3 right-3 text-green-200 transition-colors group-hover:text-green-300">
                <FaQuoteLeft className="size-6" />
            </div>

            {principalImage && review?.url && (
                <Link href={review.url} target="_blank" rel="noopener noreferrer" className="block overflow-hidden w-full h-32 rounded-t-2xl cursor-pointer group/img">
                    <img
                        src={`${ApiRoutes.ASSETS_ENDPOINT}${principalImage.url}`}
                        alt={principalImage.alt || ''}
                        className="object-cover w-full h-full transition-transform duration-300 group-hover/img:scale-110"
                    />
                </Link>
            )}

            <div className="flex flex-col flex-grow p-4">
                <div className="flex relative z-10 gap-2 items-center mb-3">
                    <SiTripadvisor className="text-main-color transition-transform size-6 group-hover:scale-110" />
                    <div className="text-xs font-semibold text-green-700">TripAdvisor Review</div>
                </div>

                <div className="relative z-10 mb-3">
                    <StarRating rating={review?.star_rating || 5} />
                </div>

                <div className="relative z-10 flex-grow mb-3">
                    <p className="text-sm italic leading-relaxed text-gray-700">"{truncateComment(review?.comment)}"</p>
                </div>

                {review?.url && (
                    <Link href={review.url} target="_blank" rel="noopener noreferrer" className="relative z-10 mb-3 text-sm text-main-color hover:underline">
                        Read more
                    </Link>
                )}

                <div className="w-12 h-0.5 bg-gradient-to-r from-green-400 to-transparent mb-3 group-hover:w-20 transition-all duration-300"></div>

                <div className="flex relative z-10 gap-3 items-center">
                    <div className="flex justify-center items-center w-10 h-10 text-sm font-bold text-white bg-gradient-to-br from-green-400 to-green-600 rounded-full shadow-md">
                        {review?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                        <div className="flex gap-1 items-center text-sm font-semibold text-gray-900">
                            {review?.name}
                            <div className="relative group/badge">
                                <PiSealCheckFill className="text-main-color size-4" />
                                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-[10px] font-medium rounded whitespace-nowrap opacity-0 invisible group-hover/badge:opacity-100 group-hover/badge:visible transition-all duration-200 z-20">
                                    Verified Customer
                                    <div className="absolute top-full left-1/2 w-0 h-0 border-t-4 border-r-4 border-l-4 -translate-x-1/2 border-l-transparent border-r-transparent border-t-gray-900"></div>
                                </div>
                            </div>
                        </div>
                        {/* <div className="flex gap-1 items-center text-xs text-gray-500">
                            <FaCheckCircle className="text-main-color size-3" />
                            {review?.created_at && format(new Date(review.created_at), 'MMM yyyy')}
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    );
}
